import {useNavigate} from 'react-router-dom';
import SignUpForm from './signUpForm/SignUpForm';
import {useState} from 'react';
import {Case, Switch} from 'react-if';
import Code from './code/Code';
import * as Yup from 'yup';
import {useTranslation} from 'react-i18next';
import {Form, Formik} from 'formik';
import validator from 'validator';
import Stage from '../components/stage/Stage';
import {getTokenToSignUp} from '../../../redux/user/userActions';
import useErrorMessages from '../../../hooks/useErrorMessages';
import Navigation from '../components/navigation/Navigation';
import {useSelector} from 'react-redux';
import setGlobalState from '../../../redux/setGlobalState';
import {resetCodeInput, updateExpiredTime} from '../../../component/SMSCode/SMSCode';
import {useEffect} from 'react';
import {hotjar} from 'react-hotjar';
import {ClASS_DOMAIN, ENROLL_DOMAIN, HOTJAR_ID, HOTJAR_SV, TICK_DOMAIN} from '../../../config/config';
import SupportComponent from '../../../layout/supportComponent/SupportComponent';
import PhoneScreen from './phoneScreen/PhoneScreen';
import {initPhoneNumber} from '../../../functions/intrernationalPhoneNumber';
import {yupStringMin} from '../../../functions/validationsYup';
import {checkUserOnLoad} from '../../../functions/authCheck';
import ExistingEmailPopup from '../../../component/existingEmailPopup/ExistingEmailPopup';
import useShow from '../../../hooks/useShow';
import UserMessage from '../../../layout/userMessages/UserMessage';
import SignUpPixels from '../../../functions/customSystemPixel/SignUpPixels';
import {fireSignUpFormSubmittedPixels} from '../../../functions/customSystemPixel/fireSignUpConversionPixels';

export const isIframe=window.parent !== window.self;
let params = new URLSearchParams(location.search);
const source = params.get('source');
export const sendPostMessage=(message)=>{
    let targetDomain = TICK_DOMAIN;
    if(source === 'enroll'){
        targetDomain = ENROLL_DOMAIN;
    } else if(source === 'classchak'){
        targetDomain = ClASS_DOMAIN;
    }
    window.parent.postMessage({buttonClick: message}, targetDomain);
};

function SignUp() {
    const [currentPage,setCurrentPage]=useState('form');
    const {t, i18n} = useTranslation('login');
    const navigate=useNavigate();
    const user = useSelector(s => s.user);

    const isMissingPhone=useSelector(s=>s.ui.login.isMissingPhone);
    const signUpLandingParams=useSelector(s=>s.ui.login?.signUpLandingParams);
    const {langloc, defaultPid, defaultEid} = useSelector((s) => s.user);
    const isIframe=window.parent !== window.self;

    useErrorMessages();

    const {open, close, isOpen, showClass} = useShow();
    const isAuthenticated = useSelector(s=>s.auth.isLoggedIn);

    useEffect(()=>{
        if(isMissingPhone){
            setCurrentPage('phone');
        }
    },[isMissingPhone]);
    useEffect(()=>{
        checkUserOnLoad().then();
        // מחובר בלי חבילה (הרשמה חלקית) — לא לזרוק לדף הבית לפני השלמת הרשמה.
        if(isAuthenticated && (defaultPid || defaultEid)){
            navigate('/');
            if(isIframe)
                sendPostMessage('signUp');
        }

        // שמירת אימייל כשמגיעים מ-login להשלמת הרשמה (isMissingPhone).
        if(!isMissingPhone) {
            setGlobalState('ui.login.existingUserEmail', null);
        }
        hotjar.initialize(parseInt(HOTJAR_ID), parseInt(HOTJAR_SV));
        const landingParams = Object.fromEntries(new URLSearchParams(location.search).entries());
        if(Object.keys(landingParams).length) {
            setGlobalState('ui.login.signUpLandingParams', landingParams);
        }
    },[]);

    const onSubmit = async (values) => {
        resetCodeInput();
        const res = await getTokenToSignUp(values);
        if(res?.noToken) {
            return;
        }
        if(res?.maskedPhone || res?.maskedEmail){
            setGlobalState('ui.login.existingEmailData', {
                maskedEmail: res.maskedEmail || '',
                maskedPhone: res.maskedPhone || '',
                newPhone: values.phone,
                user: {
                    name: `${values.nameFirst} ${values.nameLast}`,
                    email: values.email
                }
            });
            open();
            return;
        }
        if(res?.isExistingUser && !res?.noPackageOrEvent) {
            setGlobalState('ui.login.existingUserEmail', values.email);
            if(isIframe) {
                sendPostMessage('signUp');
            }
            navigate('/login');
            return;
        }
        fireSignUpFormSubmittedPixels(signUpLandingParams || Object.fromEntries(new URLSearchParams(location.search).entries()));
        setCurrentPage('code');
        updateExpiredTime();
    };
    const getTokenToSignUpXHR=useSelector(s=>s.user.getTokenToSignUpXHR);

    const codeInput=useSelector(s=>s.ui.codeInput);
    const onBack=()=>{
        const originalCodeInput={...codeInput};
        if(isMissingPhone)
            setCurrentPage('phone');
        else
            setCurrentPage('form');
        setImmediate(()=>setGlobalState('ui.codeInput',originalCodeInput));
    };

    const navClick=(index,setTouched)=>{
        if(index===0)
            onBack();
        if(index===1){
            if(getTokenToSignUpXHR==='UPDATED')
                setCurrentPage('code');
            else
                setTouched({nameLast:true,  nameFirst:true,email: true, phone:true,});
        }
    };

    useEffect(() => {
        i18n.changeLanguage(user.lang).then();
    }, [user.lang]);

    Yup.addMethod(Yup.mixed, 'phone', function(message) {
        return this.test('phone', message, function(value) {
            const country = this.resolve(Yup.ref('country'));
            const local = validator.isMobilePhoneLocales.find(local => local.includes(country));
            const phone = initPhoneNumber({nationalNumber: value, country: country});
            return validator.isMobilePhone(value || '', local) || validator.isMobilePhone('+'+phone.internationalNumber || '', local);
        });
    });

    const existingUserEmail=useSelector(s=>s.ui.login.existingUserEmail);

    return (
        <>
            <SignUpPixels />
            <title>{'Tickchak | signUp - הרשמה'}</title>
            <Formik
                initialValues={{
                    nameLast:'',
                    nameFirst:'',
                    email:existingUserEmail||'',
                    phone:'',
                    country: langloc?.split('_')[1].toUpperCase() || 'IL',
                    picture:null,
                    sendTo:'phone'}}
                validationSchema={Yup.object().shape({
                    nameLast:yupStringMin().max(30).required(),
                    nameFirst:yupStringMin().max(30).required(),
                    phone: Yup.string().phone(t('numberMessage')).required(),
                    email: Yup.string().email().required(),
                    password: yupStringMin(6,t('passwordMessage')),
                })}
                onSubmit={onSubmit}>
                {({setTouched, handleSubmit, setFieldValue})=>
                    <>
                        {isOpen && (
                            <ExistingEmailPopup
                                close={close}
                                showClass={showClass}
                                onClearPhone={() => setFieldValue('phone', '')}
                            />
                        )}
                        <Form onKeyDown={e => {
                            if(e.key === 'Enter')
                                handleSubmit();
                        }}>
                            <Stage isHeightHeader >
                                {!isIframe && <SupportComponent />}
                                <Switch >
                                    <Case condition={currentPage==='form'}>
                                        <SignUpForm/>
                                    </Case>
                                    <Case condition={currentPage==='phone'}>
                                        <PhoneScreen />
                                    </Case>
                                    <Case condition={currentPage==='code'}>
                                        <Code onBack={onBack}/>
                                    </Case>
                                </Switch>
                                <Navigation currentIndex={['form','phone'].includes(currentPage)?0:1} onClick={(index)=>navClick(index,setTouched)}/>
                            </Stage>
                        </Form>
                    </>
                }
            </Formik>
            <UserMessage />
        </>
    );
}

export default SignUp;
